import { toast } from "react-toastify";
import { useAdminLeadControllerDeleteMany } from "service";
import { ControlledSelect, ControlledSelectOption } from "ui";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "@/query-client";
import { useDialogStore } from "@/state/dialog-store";

const LEAD_BULK_ACTIONS_OPTIONS: ControlledSelectOption[] = [
  { label: "Delete", value: "delete" },
];

interface LeadsBulkActionsProps {
  leadIds: string[];
  resetSelections: () => void;
}

const LeadsBulkActions = ({
  leadIds,
  resetSelections,
}: LeadsBulkActionsProps) => {
  const { setDialog } = useDialogStore();

  const { mutate: deleteManyLeads } = useAdminLeadControllerDeleteMany({
    onSuccess: (response) => {
      const deleted = response?.data?.deletedCount ?? 0;
      const skipped = response?.data?.skippedIds?.length ?? 0;
      toast.success(
        `Deleted ${deleted} lead${deleted === 1 ? "" : "s"}` +
          (skipped
            ? `, skipped ${skipped} (already converted to a customer)`
            : ""),
      );
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/leads",
          operationId: "adminLeadControllerGetAll",
          variables: {},
        }),
      );
      resetSelections();
    },
  });

  const confirmBulkDelete = () => {
    setDialog({
      title: "Delete leads",
      description: `Are you sure you want to delete ${leadIds.length} lead${
        leadIds.length === 1 ? "" : "s"
      }? This action cannot be undone. Leads already converted to a customer are skipped.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        deleteManyLeads({ body: { ids: leadIds } });
        onClose();
      },
    });
  };

  const handleBulkAction = (value: string) => {
    if (value === "delete") {
      confirmBulkDelete();
    }
  };

  return (
    <ControlledSelect
      sx={{ marginRight: "16px" }}
      placeholder="Bulk Actions"
      options={LEAD_BULK_ACTIONS_OPTIONS}
      onChange={handleBulkAction}
      disabled={leadIds.length === 0}
    />
  );
};

export default LeadsBulkActions;
